import { useRef, useLayoutEffect, useState } from "react";
import { useTheme } from "../../App";
import { getStatus, STATUS_COLORS, STATUS_BG, CANVAS_THEME } from "../../services/thresholdUtils";
import { statusLabel as displayStatusLabel } from "../../services/metricDefinitions";

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

function GaugeInfoTip({ label, definition }) {
  const [open, setOpen] = useState(false);
  if (!definition) return null;
  return (
    <span
      style={{ position: "relative", display: "inline-flex", alignItems: "center", flexShrink: 0 }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label={`${label} definition`}
        style={{
          width: 15, height: 15, borderRadius: 999, border: "1px solid var(--teal-border)",
          background: "var(--teal-bg)", color: "var(--teal-dark)", fontSize: 10, fontWeight: 900,
          lineHeight: 1, cursor: "help", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0,
        }}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        i
      </button>
      {open && (
        <span style={{
          position: "absolute", top: 22, left: 0, width: 300, maxWidth: "min(300px, 74vw)",
          padding: "10px 12px", border: "1px solid var(--border)", borderRadius: 8,
          background: "var(--bg-card)", color: "var(--text-primary)",
          boxShadow: "0 16px 42px rgba(15, 23, 42, 0.22)", fontSize: 12,
          lineHeight: 1.45, fontWeight: 500,
        }}>
          <strong>{label}</strong>
          <br />
          {definition}
        </span>
      )}
    </span>
  );
}

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ct = CANVAS_THEME entry — uses hex status colors so zone fills work in canvas
function buildZones(thresh, gaugeMin, gaugeMax, ct) {
  if (!thresh || thresh.type === "BOOL") return [];
  if (thresh.warn === 0 && thresh.fail === 0 && thresh.pass === 0) return [];

  if (thresh.pass > 0 && thresh.warn > 0 && thresh.fail === 0) {
    const highCritical = thresh.pass * 1.15;
    return [
      { from: gaugeMin,      to: thresh.warn,  color: ct.critical },
      { from: thresh.warn,   to: thresh.pass,  color: ct.normal },
      { from: thresh.pass,   to: highCritical, color: ct.warn },
      { from: highCritical,  to: gaugeMax,     color: ct.critical },
    ];
  }

  const zones = [{ from: gaugeMin, to: thresh.warn || gaugeMax, color: ct.normal }];
  if (thresh.warn > 0) zones.push({ from: thresh.warn, to: thresh.fail || gaugeMax, color: ct.warn });
  if (thresh.fail > 0) zones.push({ from: thresh.fail, to: gaugeMax, color: ct.critical });
  return zones;
}

function calcGaugeMax(thresh, value, fallbackMax) {
  const hasThresh = thresh &&
    !(thresh.warn === 0 && thresh.fail === 0 && thresh.pass === 0) &&
    thresh.type !== "BOOL";
  const isViscosity = hasThresh && thresh.pass > 0 && thresh.warn > 0 && thresh.fail === 0;

  let gMax;
  if (hasThresh) {
    gMax = isViscosity
      ? thresh.pass * 1.6
      : ((thresh.fail > 0 ? thresh.fail * 1.5 : thresh.warn * 2.5) || fallbackMax || 100);
  } else {
    gMax = fallbackMax || 100;
  }

  if (value !== null && value !== undefined && !isNaN(value) && value > gMax) {
    gMax = value * 1.3;
  }
  return gMax;
}

function fmtValue(value, precision) {
  if (value === null || value === undefined || isNaN(value)) return "—";
  if (precision === 0) return String(Math.round(value));
  if (value > 0 && value < 1) return value.toFixed(precision + 1);
  return Number(value).toFixed(precision);
}

function drawGauge(canvas, { value, thresh, gaugeMin, gaugeMax, unit, precision, status, ct, isDark, displayValue, displayUnit, showScaleLabels = true, hideNeedleTail = false, zoneProgress = 1, needleProgress = 1 }) {
  const dpr = window.devicePixelRatio || 1;
  const W = canvas.width  / dpr;
  const H = canvas.height / dpr;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, W, H);

  const hasValue    = value !== null && value !== undefined && !isNaN(value);
  const needleColor = ct[status] || ct.na;
  const animatedValue = hasValue ? gaugeMin + (Math.min(value, gaugeMax) - gaugeMin) * needleProgress : value;
  const range  = (gaugeMax - gaugeMin) || 1;
  const cx = W / 2;
  const cy = Math.min(H - 30, 130);
  const radius = Math.min(W * 0.34, 92);
  const arcWidth = Math.max(14, Math.min(20, radius * 0.22));
  const startA = Math.PI * 0.82;
  const sweepA = Math.PI * 1.36;

  const clampVal = (v) => Math.min(Math.max(v, gaugeMin), gaugeMax);
  const ratioOf = (v) => (clampVal(v) - gaugeMin) / range;
  const angleOf = (v) => startA + ratioOf(v) * sweepA;

  const drawArc = (from, to, color, alpha, width = arcWidth) => {
    if (from >= to) return;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, angleOf(from), angleOf(to), false);
    ctx.lineWidth = width;
    ctx.strokeStyle = hexToRgba(color, alpha);
    ctx.lineCap = "round";
    ctx.stroke();
  };

  drawArc(gaugeMin, gaugeMax, ct.trackBorder, isDark ? 0.45 : 0.65, arcWidth + 2);
  drawArc(gaugeMin, gaugeMax, ct.track, 1, arcWidth);

  const zones = buildZones(thresh, gaugeMin, gaugeMax, ct);
  if (zones.length > 0) {
    const zoneLimit = gaugeMin + (gaugeMax - gaugeMin) * zoneProgress;
    zones.forEach(z => {
      const clippedTo = Math.min(z.to, zoneLimit);
      drawArc(z.from, clippedTo, z.color, isDark ? 0.78 : 0.68);
    });
  } else if (hasValue && status === "normal") {
    const zoneLimit = gaugeMin + (gaugeMax - gaugeMin) * zoneProgress;
    drawArc(gaugeMin, zoneLimit, ct.normal, isDark ? 0.7 : 0.58);
  }

  const isViscosity = thresh && thresh.pass > 0 && thresh.warn > 0 && thresh.fail === 0;
  const ticks = [];
  if (thresh && thresh.type !== "BOOL") {
    if (!isViscosity) {
      if (thresh.warn > 0) ticks.push({ v: thresh.warn,        color: ct.warn });
      if (thresh.fail > 0) ticks.push({ v: thresh.fail,        color: ct.critical });
    } else {
      ticks.push({ v: thresh.warn,        color: ct.critical });
      ticks.push({ v: thresh.pass,        color: ct.warn });
      ticks.push({ v: thresh.pass * 1.15, color: ct.critical });
    }
  }
  ticks.forEach(({ v, color }) => {
    if (v <= gaugeMin || v >= gaugeMax) return;
    const a = angleOf(v);
    const cos = Math.cos(a);
    const sin = Math.sin(a);
    ctx.beginPath();
    ctx.moveTo(cx + (radius - arcWidth / 2 - 6) * cos, cy + (radius - arcWidth / 2 - 6) * sin);
    ctx.lineTo(cx + (radius + arcWidth / 2 + 6) * cos, cy + (radius + arcWidth / 2 + 6) * sin);
    ctx.strokeStyle = color;
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.stroke();
  });

  const drawScaleText = (text, x, y, align = "center") => {
    ctx.save();
    ctx.font = "600 10px 'JetBrains Mono', monospace";
    ctx.textAlign = align;
    ctx.textBaseline = "middle";
    ctx.lineWidth = 4;
    ctx.lineJoin = "round";
    ctx.strokeStyle = isDark ? "rgba(13, 20, 34, 0.94)" : "rgba(255, 255, 255, 0.96)";
    ctx.strokeText(text, x, y);
    ctx.fillStyle = ct.scaleText;
    ctx.fillText(text, x, y);
    ctx.restore();
  };

  if (showScaleLabels) {
    drawScaleText(fmtValue(gaugeMin, 0), cx - radius - 10, cy + 32, "left");
    drawScaleText(fmtValue((gaugeMin + gaugeMax) / 2, 0), cx, cy - radius - 18, "center");
    drawScaleText(fmtValue(gaugeMax, 0), cx + radius + 10, cy + 32, "right");
  }

  if (isViscosity && thresh.pass) {
    const highA = angleOf(thresh.pass);
    const highX = cx + (radius + 26) * Math.cos(highA);
    const highY = cy + (radius + 26) * Math.sin(highA);
    ctx.save();
    ctx.font = "600 9px 'JetBrains Mono', monospace";
    ctx.fillStyle = hexToRgba(ct.normal, 0.7);
    ctx.textAlign = "center";
    ctx.fillText("HIGH", highX, highY);
    ctx.restore();
  }

  if (hasValue) {
    const needleA = angleOf(animatedValue);
    const tipR = radius - arcWidth - 8;
    const tailR = hideNeedleTail ? 0 : 18;
    const nx = cx + tipR * Math.cos(needleA);
    const ny = cy + tipR * Math.sin(needleA);
    const tx = cx - tailR * Math.cos(needleA);
    const ty = cy - tailR * Math.sin(needleA);

    ctx.save();
    ctx.shadowColor = needleColor;
    ctx.shadowBlur  = isDark ? 18 : 11;

    ctx.beginPath();
    ctx.moveTo(tx, ty);
    ctx.lineTo(nx, ny);
    ctx.strokeStyle = needleColor;
    ctx.lineWidth = 4;
    ctx.lineCap = "round";
    ctx.stroke();

    ctx.shadowBlur = 0;
    ctx.beginPath();
    ctx.arc(cx, cy, 11, 0, Math.PI * 2);
    ctx.fillStyle = needleColor;
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx, cy, 4.5, 0, Math.PI * 2);
    ctx.fillStyle = ct.hubInner;
    ctx.fill();

    ctx.restore();
  }

  const valueText = displayValue ?? fmtValue(value, precision);
  ctx.font = "700 22px 'JetBrains Mono', monospace";
  ctx.fillStyle = needleColor;
  ctx.textAlign = "right";
  ctx.fillText(valueText, W, 18);

  ctx.font = "500 10px system-ui, -apple-system, sans-serif";
  ctx.fillStyle = ct.scaleText;
  ctx.textAlign = "right";
  ctx.fillText(displayUnit ?? unit, W, 32);
}

export default function VelocityGauge({
  label,
  value,
  unit = "",
  min = 0,
  max,
  precision = 2,
  threshold,
  width = 420,
  definition = "",
  displayValue,
  displayUnit,
  showScaleLabels = true,
  embedded = false,
  compact = false,
  hideNeedleTail = false,
  engineMileage = null,
}) {
  const canvasRef = useRef(null);
  const { isDark } = useTheme();
  const ct = CANVAS_THEME[isDark ? "dark" : "light"];

  const thresh   = threshold || null;
  const hasValue = value !== null && value !== undefined && !isNaN(value);
  const hasThresh = thresh &&
    !(thresh.warn === 0 && thresh.fail === 0 && thresh.pass === 0) &&
    thresh.type !== "BOOL";
  const status   = hasValue ? (hasThresh ? getStatus(value, thresh) : (Number(value) !== 0 ? "normal" : "na")) : "na";
  const gaugeMin = min;
  const gaugeMax = calcGaugeMax(thresh, value, max);
  const H = compact ? 118 : 168;

  const isZero = hasValue && value === 0;

  useLayoutEffect(() => {
    if (isZero) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const W = canvas.offsetWidth || width;
    canvas.width  = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const render = (zoneProgress, needleProgress) => {
      drawGauge(canvas, { value, thresh, gaugeMin, gaugeMax, unit, precision, status, ct, isDark, displayValue, displayUnit, showScaleLabels, hideNeedleTail, zoneProgress, needleProgress });
    };

    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduceMotion || !hasValue) {
      render(1, 1);
      return undefined;
    }

    let frameId;
    const start = performance.now();
    const zoneMs = 650;
    const needleDelay = 360;
    const needleMs = 720;

    const tick = (now) => {
      const elapsed = now - start;
      const zoneProgress = easeOutCubic(Math.min(elapsed / zoneMs, 1));
      const needleProgress = elapsed < needleDelay ? 0 : easeOutCubic(Math.min((elapsed - needleDelay) / needleMs, 1));
      render(zoneProgress, needleProgress);
      if (elapsed < needleDelay + needleMs) {
        frameId = requestAnimationFrame(tick);
      }
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [value, thresh, gaugeMin, gaugeMax, unit, precision, status, isZero, width, ct, isDark, displayValue, displayUnit, showScaleLabels, hideNeedleTail]);

  const statusColor = STATUS_COLORS[status] || STATUS_COLORS.na;
  const statusBg    = STATUS_BG[status];
  const statusLabel = status === "na" ? null : displayStatusLabel(status, engineMileage);

  if (!hasValue || isZero) {
    return (
      <div style={{
        background: embedded ? "transparent" : "var(--dashboard-gauge-bg)",
        border: embedded ? "none" : "1px solid var(--border)",
        borderRadius: embedded ? 0 : "var(--dashboard-card-radius)",
        padding: embedded ? "10px 6px 6px" : "14px 18px 16px",
        boxShadow: embedded ? "none" : "var(--dashboard-panel-shadow)",
        minHeight: compact ? 118 : 168,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.35, color: "var(--text-secondary)", overflowWrap: "anywhere" }}>{label}</div>
          <div style={{ display: "flex", justifyContent: definition ? "space-between" : "flex-end", alignItems: "center", gap: 12, marginTop: 4 }}>
            {definition && <GaugeInfoTip label={label} definition={definition} />}
            {isZero ? (
              <span style={{ flexShrink: 0, fontFamily: "var(--font-mono)", fontSize: 16, fontWeight: 700, color: "var(--text-muted)" }}>
                {displayValue ?? "0"} <span style={{ fontSize: 10 }}>{displayUnit ?? unit}</span>
              </span>
            ) : (
              <span style={{ flexShrink: 0, fontFamily: "var(--font-mono)", fontSize: 16, fontWeight: 700, color: "var(--text-muted)" }}>—</span>
            )}
          </div>
        </div>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          flex: 1, marginTop: 12,
          color: "var(--text-muted)", fontSize: 12, fontStyle: "italic", textAlign: "center",
          border: "1px dashed var(--border)", borderRadius: 8,
          padding: "10px 12px",
        }}>
          {isZero ? `No ${label} was detected in your sample.` : `No ${label} value was reported for this sample.`}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: embedded ? "transparent" : "var(--dashboard-gauge-bg)",
      border: embedded ? "none" : `1px solid ${hasValue && status !== "na" ? ct[status] + "50" : "var(--border)"}`,
      borderRadius: embedded ? 0 : "var(--dashboard-card-radius)",
      padding: embedded ? "10px 6px 4px" : "14px 18px 12px",
      boxShadow: embedded ? "none" : "var(--dashboard-panel-shadow)",
      position: "relative",
    }}>
      <div style={{ position: "relative", zIndex: 3, minWidth: 0, marginBottom: definition ? 2 : 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.35, color: "var(--text-secondary)", overflowWrap: "anywhere" }}>{label}</div>
        {definition && (
          <div style={{ display: "flex", marginTop: 4 }}>
            <GaugeInfoTip label={label} definition={definition} />
          </div>
        )}
      </div>
      <canvas
        ref={canvasRef}
        style={{ display: "block", width: "100%", height: H }}
        aria-label={`${label}: ${fmtValue(value, precision)} ${unit}`}
      />
      {hasValue && statusLabel && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
          <span style={{
            fontSize: 10, fontWeight: 700, padding: "2px 10px", borderRadius: 20,
            background: statusBg, color: statusColor,
          }}>
            {statusLabel}
          </span>
        </div>
      )}
    </div>
  );
}
